import { useState, useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function PlayerCardView() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !playerId) return;

    let isMounted = true;
    async function loadPlayerCard() {
      try {
        const response = await api.get(`/players/${playerId}/card`);
        if (isMounted) setData(response.data);
      } catch (err) {
        console.error("[card-view] error loading stats", err);
        toast.error("Failed to load player portfolio card.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadPlayerCard();
    return () => { isMounted = false; };
  }, [playerId, user]);

  // Handle route guards
  if (authLoading) return <div className="max-w-7xl mx-auto px-6 py-12 text-zinc-400">Verifying session indices...</div>;
  if (!user) return <Navigate to="/login" replace />;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <div className="w-12 h-12 border-2 border-court border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Assembling Player Matrix...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center border border-dashed border-zinc-800">
        <p className="text-sm text-zinc-400">Requested athletic profile record could not be processed.</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-xs font-mono text-court hover:underline">← Return back</button>
      </div>
    );
  }

  const { player, stats, game_history = [] } = data;
  const fgPct = stats.fg_attempted ? ((stats.fg_made / stats.fg_attempted) * 100).toFixed(1) : "—";

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6">
      {/* Upper Navigation Action Bar */}
      <div className="mb-6 flex justify-between items-center">
        <button
          onClick={() => navigate(-1)}
          className="text-xs font-mono font-bold uppercase tracking-wider text-zinc-400 hover:text-court transition-colors flex items-center gap-1.5"
        >
          ✕ Close Card
        </button>
        <div className="text-[10px] font-mono text-zinc-600 uppercase">
          ID // {player.id?.slice(0, 8)}
        </div>
      </div>

      {/* Main Responsive Interactive Showcase Trading Card container */}
      <div className="w-full bg-zinc-950 border border-zinc-800 relative overflow-hidden rounded-lg shadow-2xl group transition-all duration-300 hover:border-zinc-700">
        {/* Decorative Court Grid Background Accents */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-25 pointer-events-none" />
        
        {/* Top Court Coloring Accent Trim Line */}
        <div className="h-1.5 w-full bg-court" />

        <div className="p-6 sm:p-10 grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-8 relative z-10">
          
          {/* LEFT PANEL: Avatar Badge + Bio Index Summary */}
          <div className="flex flex-col items-center text-center border-b md:border-b-0 md:border-r border-zinc-900 pb-6 md:pb-0 md:pr-8">
            {/* Holographic Jersey Number Silhouette Badge Circle */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 rounded-full flex flex-col items-center justify-center relative shadow-inner group-hover:border-court transition-colors">
              <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">POS</span>
              <span className="font-display font-black text-2xl uppercase text-white tracking-tighter mt-0.5">
                {player.position ? player.position.split(" ")[0] : "—"}
              </span>
            </div>

            {/* General Profile Titles */}
            <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight mt-5 text-white leading-none">
              {player.name || "Unknown Prospect"}
            </h2>
            
            {player.location && (
              <div className="text-xs text-zinc-400 font-mono mt-2 flex items-center gap-1">
                <span className="text-court">📍</span> {player.location}
              </div>
            )}

            {/* Metrics Spec Line Info */}
            <div className="grid grid-cols-2 gap-4 w-full mt-6 pt-6 border-t border-zinc-900/60 font-mono text-center">
              <div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Age</div>
                <div className="text-lg font-bold text-zinc-200 mt-0.5">{player.age ? `${player.age} yrs` : "—"}</div>
              </div>
              <div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Height</div>
                <div className="text-lg font-bold text-zinc-200 mt-0.5">{player.height_cm ? `${player.height_cm} cm` : "—"}</div>
              </div>
            </div>

            {/* Social Anchor Link Mappings */}
            {player.instagram && (
              <a
                href={player.instagram.startsWith("http") ? player.instagram : `https://${player.instagram}`}
                target="_blank"
                rel="noreferrer"
                className="w-full mt-6 py-2 border border-zinc-800 bg-zinc-900/30 hover:bg-zinc-900 text-xs font-mono text-zinc-300 tracking-wide transition-all uppercase rounded text-center block"
              >
                Instagram Profile ↗
              </a>
            )}
          </div>

          {/* RIGHT PANEL: Live Averages + Cumulative Metrics Ledger */}
          <div className="flex flex-col justify-between">
            <div>
              {/* Highlight Headline Box Score Section */}
              <div className="text-[10px] font-mono font-bold text-court tracking-[0.25em] uppercase mb-3">
                Season Per-Game Averages
              </div>
              
              <div className="grid grid-cols-5 gap-2 text-center bg-zinc-900/30 border border-zinc-900 p-3 sm:p-4 rounded-md">
                {[
                  ["PPG", stats.ppg],
                  ["RPG", stats.rpg],
                  ["APG", stats.apg],
                  ["SPG", stats.spg],
                  ["BPG", stats.bpg],
                ].map(([lbl, val]) => (
                  <div key={lbl}>
                    <div className="text-[9px] sm:text-[10px] font-mono text-zinc-500 uppercase tracking-tight">{lbl}</div>
                    <div className="text-xl sm:text-2xl font-display font-black text-white mt-1">{val}</div>
                  </div>
                ))}
              </div>

              {/* Total Accumulated Box Metrics Section Grid */}
              <div className="text-[10px] font-mono font-bold text-zinc-500 tracking-[0.25em] uppercase mt-8 mb-3">
                Career Cumulative Totals
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ["Games Played", stats.games_played],
                  ["Total Points", stats.points],
                  ["Total Rebounds", stats.rebounds],
                  ["Total Assists", stats.assists],
                  ["Total Steals", stats.steals],
                  ["Total Blocks", stats.blocks],
                  ["Field Goal %", `${fgPct}%`],
                  ["Total Fouls", stats.fouls],
                ].map(([title, metric]) => (
                  <div key={title} className="border border-zinc-900 bg-zinc-950/80 p-3 rounded">
                    <div className="text-[10px] font-mono text-zinc-500 leading-none">{title}</div>
                    <div className="text-lg font-display font-bold text-zinc-200 mt-1.5 tracking-tight">{metric}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Verified Achievements Accolade Tags Footer */}
            {player.achievements && player.achievements.length > 0 && (
              <div className="mt-8 pt-6 border-t border-zinc-900">
                <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2.5">
                  Verified Honors & Accolades:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {player.achievements.map((ach, idx) => (
                    <span 
                      key={idx} 
                      className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs px-2.5 py-1 rounded shadow-sm"
                    >
                      🏆 {ach}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Standard Bio text block if loaded */}
            {player.bio && (
              <div className="mt-6 text-xs text-zinc-500 font-mono italic leading-relaxed border-l-2 border-zinc-800 pl-3">
                &ldquo;{player.bio}&rdquo;
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.3em] text-court">Recent Game History</div>
          <div className="text-xs text-zinc-500">{game_history.length} games</div>
        </div>
        {game_history.length === 0 ? (
          <div className="border border-zinc-800 rounded-lg p-6 text-sm text-zinc-500">No recorded game history yet.</div>
        ) : (
          <div className="space-y-3">
            {game_history.map((entry) => (
              <div key={`${entry.game_id}-${entry.team_name}`} className="border border-zinc-800 rounded-lg p-4 bg-zinc-950/70">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm text-white font-bold">{entry.team_name} vs {entry.opponent_team_name}</div>
                    <div className="text-xs text-zinc-400 mt-1">{entry.created_at ? new Date(entry.created_at).toLocaleString() : "No date"}</div>
                  </div>
                  <div className="text-xs uppercase tracking-[0.25em] text-court">{entry.status}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm text-zinc-300">
                  <div><span className="text-zinc-400">Score</span><div className="text-white">{entry.score_a} - {entry.score_b}</div></div>
                  <div><span className="text-zinc-400">Points</span><div className="text-white">{entry.points}</div></div>
                  <div><span className="text-zinc-400">Rebounds</span><div className="text-white">{entry.rebounds_off + entry.rebounds_def}</div></div>
                  <div><span className="text-zinc-400">Assists</span><div className="text-white">{entry.assists}</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}